import { ComponentRef, forwardRef, useMemo, useEffect, useState} from "react";
import { alpha, Button, ButtonProps, CircularProgress, IconButton, IconButtonProps } from "@mui/material";
import { Check, ContentCopy, Error } from "@mui/icons-material";

enum CopyState {
    Running = 'running',
    Success = 'success',
    Error = 'error',
}

const CopyButton = forwardRef<
    ComponentRef<typeof IconButton | typeof Button>,
    (IconButtonProps | ButtonProps) & { content: string, buttonType?: 'icon' | 'button' }
>(
    ({ children, content, buttonType = 'icon', ...props }, ref) => {

        const [copyState, setCopyState] = useState<CopyState | null>(null);

        const copyStateIcon = useMemo(() => {
            switch (copyState) {
                case CopyState.Running:
                    return <CircularProgress size={20} />;
                case CopyState.Success:
                    return <Check />;
                case CopyState.Error:
                    return <Error />;
                default:
                    return children || <ContentCopy />;
            }
        }, [children, copyState]);

        const color: IconButtonProps['color'] | ButtonProps['color'] = useMemo(() => {
            switch (copyState) {
                case CopyState.Success:
                    return "success";
                case CopyState.Error:
                    return "error";
                default:
                    return props.color || 'inherit';
            }
        }, [copyState, props.color]);

        useEffect(() => {
            if (copyState === CopyState.Success || copyState === CopyState.Error) {
                setTimeout(() => setCopyState(null), 1500);
            }
        }, [copyState]);

        function copyContentToClipboard() {
            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                setCopyState(CopyState.Running);
                navigator.clipboard.writeText(content)
                    .then(() => {
                        setCopyState(CopyState.Success);
                    })
                    .catch((err: unknown) => {
                        if (err instanceof Error) {
                            console.error('Failed to copy to clipboard: ', (err as Error).message);
                        } else {
                            console.error('Failed to copy to clipboard: ', err);
                        }
                        setCopyState(CopyState.Error);
                    });
            }
        }

        switch (buttonType) {
            case 'icon':
                return (
                    <IconButton
                        ref={ref}
                        onClick={copyContentToClipboard}
                        color={color}
                        disabled={copyState == CopyState.Running}
                        {...props}
                    >
                        {copyStateIcon}
                    </IconButton>
                )
            case 'button':
                return (
                    <Button
                        aria-label="Kopieren"
                        variant="outlined"
                        // @ts-expect-error works fine
                        color={color}
                        disabled={copyState == CopyState.Running}
                        onClick={copyContentToClipboard}
                        sx={{
                            padding: (theme) => theme.spacing(1),
                            width: 'auto',
                            backgroundColor: (theme) =>alpha(theme.palette.background.paper, .75),
                            borderRadius: 1,
                            color: (theme) => theme.palette.primary.light,
                            '&:hover': {
                                scale: 1.05,
                                backgroundColor: (theme) => alpha(theme.palette.background.paper, .9)
                            },
                            ...(props.sx || {}),
                        }}
                        {...props}
                    >
                        {copyStateIcon}
                    </Button>
                )
            default: return null;
        }
    }
)

CopyButton.displayName = "CopyLinkButton";
export default CopyButton;
